import React, { useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ScrollView, Alert, ActivityIndicator, Linking, Platform, Modal } from 'react-native';
import AppTextInput from '../components/AppTextInput';
import { AuthManager } from '../utils/authManager';
import { UnifiedAuthService, type CareRole } from '../services/unifiedAuthService';
import { geocodingService } from '../services/geocodingService';
import { COUNTRIES, getStatesForCountry } from '../constants/states';
import customerWelcomeEmailService from '../services/customerWelcomeEmailService';

interface CustomerRegistrationScreenProps {
  onBack: () => void;
  onRegistrationComplete: () => void;
  /** For care coordination sign-up: family | senior | professional. Stored on user and used for care dashboard routing. */
  careRole?: CareRole;
}

const CustomerRegistrationScreen: React.FC<CustomerRegistrationScreenProps> = ({
  onBack,
  onRegistrationComplete,
  careRole,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    address: '',
    city: '',
    state: '',
    zipcode: '',
    country: 'USA', // Default to USA
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [showStateDropdown, setShowStateDropdown] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const validateForm = () => {
    console.log('🔍 CustomerRegistration: Starting form validation');
    const newErrors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
      console.log('❌ Validation: Name is missing');
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
      console.log('❌ Validation: Email is missing');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
      console.log('❌ Validation: Email format is invalid');
    }

    // Phone is optional (App Store 5.1.1); validate format only if provided
    if (formData.phone.trim() && !/^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/.test(formData.phone)) {
      newErrors.phone = 'Phone number is invalid';
      console.log('❌ Validation: Phone format is invalid');
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
      console.log('❌ Validation: Password is missing');
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
      console.log('❌ Validation: Passwords do not match');
    }

    // Full address is optional (App Store 5.1.1); validate format only when provided
    if (formData.zipcode.trim()) {
      if (!geocodingService.isValidZipcode(formData.zipcode, formData.country)) {
        newErrors.zipcode =
          'Enter a valid US ZIP (5 digits), Canadian postal code (A1A 1A1), or India PIN (6 digits).';
        console.log('❌ Validation: Zipcode format is invalid:', formData.zipcode);
      } else {
        // Auto-detect country from zipcode format
        const trimmedZ = formData.zipcode.trim();
        const isUSZipcode = /^\d{5}$/.test(trimmedZ);
        const isCanadianPostal = /^[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d$/.test(trimmedZ);
        const isIndiaPin = /^\d{6}$/.test(trimmedZ);

        if (isUSZipcode && formData.country !== 'USA') {
          setFormData(prev => ({ ...prev, country: 'USA', state: '' }));
        } else if (isCanadianPostal && formData.country !== 'Canada') {
          setFormData(prev => ({ ...prev, country: 'Canada', state: '' }));
        } else if (isIndiaPin && formData.country !== 'India') {
          setFormData(prev => ({ ...prev, country: 'India', state: '' }));
        }
      }
    }

    setErrors(newErrors);
    const isValid = Object.keys(newErrors).length === 0;
    console.log('🔍 CustomerRegistration: Validation complete. Valid:', isValid, 'Errors:', Object.keys(newErrors));
    return isValid;
  };

  const handleRegister = async () => {
    console.log('🔵 CustomerRegistration: handleRegister called');
    console.log('🔵 CustomerRegistration: Form data:', formData);
    console.log('🔵 CustomerRegistration: Terms accepted:', termsAccepted);
    
    const isValid = validateForm();
    console.log('🔵 CustomerRegistration: Form validation result:', isValid);
    
    if (!isValid) {
      console.log('❌ CustomerRegistration: Form validation failed');
      return;
    }

    if (!termsAccepted) {
      console.log('❌ CustomerRegistration: Terms not accepted');
      Alert.alert(
        'Terms and Privacy Policy Required',
        'You must accept the Terms of Service and Privacy Policy to create an account.',
        [{ text: 'OK' }]
      );
      return;
    }

    console.log('✅ CustomerRegistration: Starting registration process...');
    setIsLoading(true);

    try {
      console.log('🔵 CustomerRegistration: Calling UnifiedAuthService.registerUser...');
      
      // Register user with Firebase Auth
      const user = await UnifiedAuthService.registerUser(
        formData.email,
        formData.password,
        {
          name: formData.name,
          phone: formData.phone.trim() || '',
          userType: 'customer',
          address: {
            street: formData.address.trim() || '',
            city: formData.city.trim() || '',
            state: formData.state.trim() || '',
            zipcode: formData.zipcode.trim() || '',
            country: formData.country || '',
          },
          ...(careRole != null && { careRole }),
        }
      );
      
      console.log('✅ CustomerRegistration: Firebase Auth registration successful:', user.email);
      console.log('✅ CustomerRegistration: User ID:', user.id);

      // Send welcome email (async, don't block UI)
      customerWelcomeEmailService.sendWelcomeEmail({
        id: user.id,
        email: user.email,
        name: formData.name,
      }).catch(error => {
        console.error('Failed to send welcome email:', error);
        // Don't show error to user - email sending failure shouldn't block registration
      });

      console.log('✅ CustomerRegistration: Registration successful, calling onRegistrationComplete');
      // Navigate directly to user dashboard without showing alert
      onRegistrationComplete();
    } catch (error: any) {
      console.error('❌ Registration error:', error);
      console.error('❌ Error type:', typeof error);
      console.error('❌ Error code:', error?.code);
      console.error('❌ Error message:', error?.message);
      console.error('❌ Error stack:', error?.stack);
      console.error('❌ Full error:', JSON.stringify(error, Object.getOwnPropertyNames(error), 2));
      
      // Handle specific error types with user-friendly messages
      let errorMessage = error?.message || error?.toString() || 'Failed to create account. Please try again.';
      let errorTitle = 'Registration Failed';
      
      if (error?.code === 'permission-denied' || error?.message?.includes('permission-denied')) {
        errorTitle = 'Firestore Permission Error';
        errorMessage = 'Your account was created, but we couldn\'t save your profile data due to security rules.\n\n' +
          'This is a configuration issue. Please contact support or check Firebase Console → Firestore Database → Rules.\n\n' +
          'Your account exists and you can try logging in, but some features may not work until this is fixed.';
      } else if (error?.code === 'sync-failed' || error?.userMessage) {
        errorTitle = 'Account Created - Sync Issue';
        errorMessage = error.userMessage || 'Your account was created successfully! We\'re having trouble syncing your profile data. Don\'t worry - your account is saved and we\'ll sync your data automatically when your connection improves.';
      } else if (error?.code === 'auth/email-already-in-use') {
        errorTitle = 'Email Already Registered';
        errorMessage = 'This email is already registered. Please try logging in instead, or use "Forgot Password" to reset your password.';
      } else if (error?.code === 'auth/weak-password') {
        errorTitle = 'Weak Password';
        errorMessage = 'Password is too weak. Please use at least 6 characters.';
      } else if (error?.code === 'auth/invalid-email') {
        errorTitle = 'Invalid Email';
        errorMessage = 'Please enter a valid email address.';
      }
      
      // Handle sync errors gracefully - account was created, just sync failed
      if (error?.code === 'sync-failed' || error?.code === 'sync-timeout') {
        // Account was created successfully, but Firestore sync failed
        // This is OK - user can still use the app, data will sync in background
        const userMessage = error?.userMessage || 'Your account was created successfully! We\'re syncing your profile data in the background.';
        
        if (Platform.OS === 'web') {
          // Show a non-blocking notification
          const proceed = window.confirm(
            userMessage + '\n\nYou can continue using the app now. Your data will sync automatically.'
          );
          if (proceed) {
            onRegistrationComplete();
          }
        } else {
          Alert.alert(
            'Account Created!',
            userMessage + '\n\nYou can continue using the app now. Your data will sync automatically.',
            [{ text: 'Continue', onPress: onRegistrationComplete }]
          );
        }
        return; // Don't show error, account was created
      }
      
      // Check if email is already registered (check both error code and message)
      if (error.code === 'auth/email-already-in-use' ||
          errorMessage.toLowerCase().includes('already registered') || 
          errorMessage.toLowerCase().includes('email-already-in-use')) {
        // Use web-compatible alert
        if (Platform.OS === 'web') {
          const goToLogin = window.confirm(
            'This email is already registered. Would you like to log in instead?'
          );
          if (goToLogin) {
            onBack();
          }
        } else {
          Alert.alert(
            'Email Already Registered',
            'This email is already registered. Would you like to log in instead?',
            [
              {
                text: 'Cancel',
                style: 'cancel',
              },
              {
                text: 'Go to Login',
                onPress: () => {
                  // Navigate back to login screen
                  onBack();
                },
              },
            ]
          );
        }
      } else if (errorMessage.toLowerCase().includes('firebase') || 
                 errorMessage.toLowerCase().includes('not properly configured') ||
                 errorMessage.toLowerCase().includes('cannot read properties of null')) {
        // Use web-compatible alert
        if (Platform.OS === 'web') {
          window.alert('Configuration Error: Firebase is not properly configured. Please refresh the page and try again. If the problem persists, contact support.');
        } else {
          Alert.alert(
            'Configuration Error',
            'Firebase is not properly configured. Please refresh the page and try again. If the problem persists, contact support.',
            [{ text: 'OK' }]
          );
        }
      } else {
        // Use web-compatible alert
        if (Platform.OS === 'web') {
          window.alert(`${errorTitle}: ${errorMessage}`);
        } else {
          Alert.alert(errorTitle, errorMessage);
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const updateFormData = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    setErrors(prev => {
      if (prev[field]) {
        return { ...prev, [field]: '' };
      }
      return prev;
    });
  };

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={onBack} style={styles.backButton}>
          <Text style={styles.backButtonText}>← Back</Text>
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Create Account</Text>
        <View style={{ width: 50 }} />
      </View>

      <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
        <View style={styles.content}>
          <Text style={styles.title}>Welcome to BookAPT!</Text>
          <Text style={styles.subtitle}>
            Create your account to start booking appointments instantly
          </Text>

          {/* Personal Information */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Personal Information</Text>

            <View style={styles.inputContainer}>
              <Text style={styles.label}>
                Full Name <Text style={styles.required}>*</Text>
              </Text>
              <AppTextInput
                style={[styles.input, errors.name && styles.inputError]}
                placeholder=""
                value={formData.name}
                onChangeText={(value) => updateFormData('name', value)}
                autoCapitalize="words"
                autoComplete="name"
                textContentType="name"
              />
              {errors.name && <Text style={styles.errorText}>{errors.name}</Text>}
            </View>

            <View style={styles.inputContainer}>
              <Text style={styles.label}>
                Email <Text style={styles.required}>*</Text>
              </Text>
              <AppTextInput
                style={[styles.input, errors.email && styles.inputError]}
                placeholder=""
                value={formData.email}
                onChangeText={(value) => updateFormData('email', value)}
                keyboardType="email-address"
                autoCapitalize="none"
                autoComplete="email"
                textContentType="emailAddress"
              />
              {errors.email && <Text style={styles.errorText}>{errors.email}</Text>}
            </View>

            <View style={styles.inputContainer}>
              <Text style={styles.label}>Phone Number (Optional)</Text>
              <AppTextInput
                style={[styles.input, errors.phone && styles.inputError]}
                placeholder=""
                value={formData.phone}
                onChangeText={(value) => updateFormData('phone', value)}
                keyboardType="phone-pad"
                autoComplete="tel"
                textContentType="telephoneNumber"
              />
              {errors.phone && <Text style={styles.errorText}>{errors.phone}</Text>}
            </View>
          </View>

          {/* Address Information - Optional per App Store 5.1.1 */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Address (Optional)</Text>

            <View style={styles.inputContainer}>
              <Text style={styles.label}>Street Address</Text>
              <AppTextInput
                style={styles.input}
                placeholder=""
                value={formData.address}
                onChangeText={(value) => updateFormData('address', value)}
                autoComplete="street-address"
                textContentType="streetAddressLine1"
              />
            </View>

            <View style={styles.inputContainer}>
              <Text style={styles.label}>City</Text>
              <AppTextInput
                style={[styles.input, errors.city && styles.inputError]}
                placeholder=""
                value={formData.city}
                onChangeText={(value) => updateFormData('city', value)}
                autoCapitalize="words"
                autoComplete="address-level2"
                textContentType="addressCity"
              />
              {errors.city && <Text style={styles.errorText}>{errors.city}</Text>}
            </View>

            <View style={styles.inputContainer}>
              <Text style={styles.label}>Country</Text>
              <TouchableOpacity
                style={[styles.input, styles.dropdownButton, errors.country && styles.inputError]}
                onPress={() => setShowCountryDropdown(true)}
              >
                <Text style={[styles.dropdownButtonText, !formData.country && styles.placeholderText]}>
                  {formData.country 
                    ? COUNTRIES.find(c => c.code === formData.country)?.name || formData.country
                    : 'Select Country'}
                </Text>
                <Text style={styles.dropdownArrow}>▼</Text>
              </TouchableOpacity>
              {errors.country && <Text style={styles.errorText}>{errors.country}</Text>}
            </View>

            <View style={styles.inputContainer}>
              <Text style={styles.label}>State/Province</Text>
              <TouchableOpacity
                style={[styles.input, styles.dropdownButton, errors.state && styles.inputError]}
                onPress={() => {
                  if (!formData.country) {
                    Alert.alert('Select Country First', 'Please select a country before choosing a state/province.');
                    return;
                  }
                  setShowStateDropdown(true);
                }}
              >
                <Text style={[styles.dropdownButtonText, !formData.state && styles.placeholderText]}>
                  {formData.state || (formData.country === 'Canada' ? 'Select Province' : 'Select State')}
                </Text>
                <Text style={styles.dropdownArrow}>▼</Text>
              </TouchableOpacity>
              {errors.state && <Text style={styles.errorText}>{errors.state}</Text>}
            </View>

            <View style={styles.inputContainer}>
              <Text style={styles.label}>Zipcode</Text>
              <AppTextInput
                style={[styles.input, errors.zipcode && styles.inputError]}
                placeholder=""
                value={formData.zipcode}
                onChangeText={(value) => updateFormData('zipcode', value)}
                autoCapitalize="characters"
                maxLength={7}
                autoComplete="postal-code"
                textContentType="postalCode"
              />
              {errors.zipcode && <Text style={styles.errorText}>{errors.zipcode}</Text>}
            </View>
          </View>

          {/* Security */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Security</Text>

            <View style={styles.inputContainer}>
              <Text style={styles.label}>
                Password <Text style={styles.required}>*</Text>
              </Text>
              <View style={[styles.passwordInputContainer, errors.password && styles.inputErrorBorder]}>
                <AppTextInput
                  style={styles.passwordInput}
                  placeholder=""
                  value={formData.password}
                  onChangeText={(value) => updateFormData('password', typeof value === 'string' ? value : '')}
                  secureTextEntry={!showPassword}
                  textContentType="newPassword"
                  autoComplete="new-password"
                />
                <TouchableOpacity
                  style={styles.eyeIconButton}
                  onPress={() => setShowPassword(!showPassword)}
                >
                  <Text style={styles.eyeIcon}>{showPassword ? '👁️' : '👁️‍🗨️'}</Text>
                </TouchableOpacity>
              </View>
              {errors.password && <Text style={styles.errorText}>{errors.password}</Text>}
            </View>

            <View style={styles.inputContainer}>
              <Text style={styles.label}>
                Confirm Password <Text style={styles.required}>*</Text>
              </Text>
              <View style={[styles.passwordInputContainer, errors.confirmPassword && styles.inputErrorBorder]}>
                <AppTextInput
                  style={styles.passwordInput}
                  placeholder=""
                  value={formData.confirmPassword}
                  onChangeText={(value) => updateFormData('confirmPassword', typeof value === 'string' ? value : '')}
                  secureTextEntry={!showConfirmPassword}
                  textContentType="newPassword"
                  autoComplete="new-password"
                />
                <TouchableOpacity
                  style={styles.eyeIconButton}
                  onPress={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  <Text style={styles.eyeIcon}>{showConfirmPassword ? '👁️' : '👁️‍🗨️'}</Text>
                </TouchableOpacity>
              </View>
              {errors.confirmPassword && (
                <Text style={styles.errorText}>{errors.confirmPassword}</Text>
              )}
            </View>
          </View>

          {/* Terms and Privacy Policy Agreement */}
          <View style={styles.termsContainer}>
            <TouchableOpacity
              style={styles.checkboxContainer}
              onPress={() => setTermsAccepted(!termsAccepted)}
              activeOpacity={0.7}
            >
              <View style={[styles.checkbox, termsAccepted && styles.checkboxChecked]}>
                {termsAccepted && <Text style={styles.checkmark}>✓</Text>}
              </View>
              <Text style={styles.termsLabel}>
                I agree to the{' '}
                <Text
                  style={styles.termsLink}
                  onPress={async () => {
                    try {
                      const url = 'https://bookapt.io/terms.html';
                      if (Platform.OS === 'web' && typeof window !== 'undefined') {
                        window.open(url, '_blank', 'noopener,noreferrer');
                      } else {
                        const supported = await Linking.canOpenURL(url);
                        if (supported) {
                          await Linking.openURL(url);
                        } else {
                          Alert.alert('Error', 'Cannot open Terms of Service link');
                        }
                      }
                    } catch (error) {
                      Alert.alert('Error', 'Failed to open Terms of Service');
                    }
                  }}
                >
                  Terms of Service
                </Text>
                {' '}and{' '}
                <Text
                  style={styles.termsLink}
                  onPress={async () => {
                    try {
                      const url = 'https://bookapt.io/privacy.html';
                      if (Platform.OS === 'web' && typeof window !== 'undefined') {
                        window.open(url, '_blank', 'noopener,noreferrer');
                      } else {
                        const supported = await Linking.canOpenURL(url);
                        if (supported) {
                          await Linking.openURL(url);
                        } else {
                          Alert.alert('Error', 'Cannot open Privacy Policy link');
                        }
                      }
                    } catch (error) {
                      Alert.alert('Error', 'Failed to open Privacy Policy');
                    }
                  }}
                >
                  Privacy Policy
                </Text>
              </Text>
            </TouchableOpacity>
            {!termsAccepted && errors.terms && (
              <Text style={styles.errorText}>{errors.terms}</Text>
            )}
          </View>

          {/* Register Button */}
          <TouchableOpacity
            style={[
              styles.registerButton,
              (isLoading || !termsAccepted) && styles.registerButtonDisabled,
            ]}
            onPress={() => {
              console.log('🔵 CustomerRegistration: Register button pressed');
              console.log('🔵 CustomerRegistration: isLoading:', isLoading, 'termsAccepted:', termsAccepted);
              handleRegister();
            }}
            disabled={isLoading || !termsAccepted}
          >
            {isLoading ? (
              <ActivityIndicator color="#fff" />
            ) : (
              <Text style={styles.registerButtonText}>Create Account</Text>
            )}
          </TouchableOpacity>
        </View>
      </ScrollView>

      {/* Country Dropdown Modal */}
      <Modal
        visible={showCountryDropdown}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setShowCountryDropdown(false)}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowCountryDropdown(false)}
        >
          <View style={styles.dropdownModal} onStartShouldSetResponder={() => true}>
            <View style={styles.dropdownHeader}>
              <Text style={styles.dropdownTitle}>Select Country</Text>
              <TouchableOpacity onPress={() => setShowCountryDropdown(false)}>
                <Text style={styles.dropdownClose}>✕</Text>
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.dropdownList}>
              {COUNTRIES.map((country) => (
                <TouchableOpacity
                  key={country.code}
                  style={styles.dropdownOption}
                  onPress={() => {
                    // Clear state when country changes
                    setFormData(prev => {
                      const newData = { ...prev, country: country.code };
                      if (prev.country !== country.code) {
                        newData.state = '';
                      }
                      return newData;
                    });
                    // Clear error when user changes country
                    if (errors.country) {
                      setErrors(prev => ({ ...prev, country: '' }));
                    }
                    setShowCountryDropdown(false);
                  }}
                >
                  <Text style={styles.dropdownOptionText}>{country.name}</Text>
                  {formData.country === country.code && (
                    <Text style={styles.dropdownCheckmark}>✓</Text>
                  )}
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </TouchableOpacity>
      </Modal>

      {/* State Dropdown Modal */}
      <Modal
        visible={showStateDropdown}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setShowStateDropdown(false)}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowStateDropdown(false)}
        >
          <View style={styles.dropdownModal} onStartShouldSetResponder={() => true}>
            <View style={styles.dropdownHeader}>
              <Text style={styles.dropdownTitle}>
                Select {formData.country === 'Canada' ? 'Province' : 'State'}
              </Text>
              <TouchableOpacity onPress={() => setShowStateDropdown(false)}>
                <Text style={styles.dropdownClose}>✕</Text>
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.dropdownList}>
              {getStatesForCountry(formData.country).map((state) => (
                <TouchableOpacity
                  key={state.code}
                  style={styles.dropdownOption}
                  onPress={() => {
                    updateFormData('state', state.code);
                    setShowStateDropdown(false);
                  }}
                >
                  <Text style={styles.dropdownOptionText}>
                    {state.code} - {state.name}
                  </Text>
                  {formData.state === state.code && (
                    <Text style={styles.dropdownCheckmark}>✓</Text>
                  )}
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </TouchableOpacity>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8f9fa',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingTop: 60,
    paddingBottom: 20,
    backgroundColor: 'white',
    borderBottomWidth: 1,
    borderBottomColor: '#e1e5e9',
  },
  backButton: {
    padding: 5,
  },
  backButtonText: {
    fontSize: 16,
    color: '#007AFF',
    fontWeight: '600',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1C1C1E',
  },
  scrollView: {
    flex: 1,
  },
  content: {
    padding: 20,
    paddingBottom: 40,
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 16,
    color: '#8E8E93',
    marginBottom: 32,
    lineHeight: 22,
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 16,
  },
  inputContainer: {
    marginBottom: 16,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1C1C1E',
    marginBottom: 8,
  },
  required: {
    color: '#FF3B30',
  },
  dropdownButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingRight: 12,
  },
  dropdownButtonText: {
    fontSize: 16,
    color: '#1C1C1E',
    flex: 1,
  },
  placeholderText: {
    color: '#8E8E93',
  },
  dropdownArrow: {
    fontSize: 12,
    color: '#8E8E93',
    marginLeft: 8,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  dropdownModal: {
    backgroundColor: 'white',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '70%',
    paddingBottom: Platform.OS === 'ios' ? 20 : 0,
  },
  dropdownHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#e1e5e9',
  },
  dropdownTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1C1C1E',
  },
  dropdownClose: {
    fontSize: 24,
    color: '#8E8E93',
    padding: 5,
  },
  dropdownList: {
    maxHeight: 400,
  },
  dropdownOption: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  dropdownOptionText: {
    fontSize: 16,
    color: '#1C1C1E',
    flex: 1,
  },
  dropdownCheckmark: {
    fontSize: 18,
    color: '#007AFF',
    fontWeight: 'bold',
    marginLeft: 10,
  },
  input: {
    backgroundColor: 'white',
    borderWidth: 1,
    borderColor: '#C7C7CC',
    borderRadius: 10,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 16,
    color: '#1C1C1E',
  },
  inputError: {
    borderColor: '#FF3B30',
  },
  errorText: {
    color: '#FF3B30',
    fontSize: 12,
    marginTop: 4,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  registerButton: {
    backgroundColor: '#007AFF',
    borderRadius: 12,
    paddingVertical: 16,
    alignItems: 'center',
    marginTop: 16,
    shadowColor: '#007AFF',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 6,
  },
  registerButtonDisabled: {
    backgroundColor: '#C7C7CC',
  },
  registerButtonText: {
    color: 'white',
    fontSize: 18,
    fontWeight: 'bold',
  },
  termsContainer: {
    marginBottom: 16,
    marginTop: 8,
  },
  checkboxContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  checkbox: {
    width: 24,
    height: 24,
    borderRadius: 6,
    borderWidth: 2,
    borderColor: '#C7C7CC',
    marginRight: 12,
    marginTop: 2,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'white',
  },
  checkboxChecked: {
    backgroundColor: '#007AFF',
    borderColor: '#007AFF',
  },
  checkmark: {
    color: 'white',
    fontSize: 16,
    fontWeight: 'bold',
  },
  termsLabel: {
    flex: 1,
    fontSize: 13,
    color: '#1C1C1E',
    lineHeight: 20,
  },
  termsLink: {
    color: '#007AFF',
    textDecorationLine: 'underline',
    fontWeight: '600',
  },
  passwordInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'white',
    borderWidth: 1,
    borderColor: '#C7C7CC',
    borderRadius: 10,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  passwordInput: {
    flex: 1,
    fontSize: 16,
    color: '#1C1C1E',
    paddingVertical: 0,
  },
  inputErrorBorder: {
    borderColor: '#FF3B30',
  },
  eyeIconButton: {
    padding: 4,
    marginLeft: 8,
  },
  eyeIcon: {
    fontSize: 20,
  },
});

export default CustomerRegistrationScreen;

